/* Nanref 3D viewer (v18 preview, Oct 31 2026; v18b: first thing in the photo gallery)
   - Moves the viewer over the main photo of the product gallery and adds a "3D" button
     before the photo thumbnails. A photo thumbnail shows the photos; "3D" brings the model back.
   - Loads Google model-viewer 4.0.0 only when the viewer is close to the screen.
   - Part chips and hotspots move the camera to that part; Reset / double-click returns.
   - Slow auto-turn until the visitor touches it; none with reduced motion.
   v19: "See it in your room" AR on phones (real size, ar-scale fixed). On computers without AR, a button
   shows a QR code (qrcode-generator 1.4.4, loaded only when opened) that opens this page on a phone.
   v22: picking another color (variant) makes the theme rebuild the photo gallery, which threw the 3D viewer out.
   The viewer now puts itself back into the new gallery: it shows the photo of the chosen color, and the
   "3D" button (or "View in 3D" on phones) brings the same, already-loaded model back. */
(() => {
  const MV_SRC = 'https://cdn.jsdelivr.net/npm/@google/model-viewer@4.0.0/dist/model-viewer.min.js';
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  let mvPromise = null;
  const loadModelViewer = () => {
    if (customElements.get('model-viewer')) return Promise.resolve();
    if (!mvPromise) {
      mvPromise = new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.type = 'module';
        s.src = MV_SRC;
        s.onload = () => customElements.whenDefined('model-viewer').then(resolve);
        s.onerror = reject;
        document.head.appendChild(s);
      });
    }
    return mvPromise;
  };

  const QR_SRC = 'https://cdn.jsdelivr.net/npm/qrcode-generator@1.4.4/qrcode.min.js';
  let qrPromise = null;
  const loadQr = () => {
    if (window.qrcode) return Promise.resolve();
    if (!qrPromise) {
      qrPromise = new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = QR_SRC;
        s.onload = resolve;
        s.onerror = reject;
        document.head.appendChild(s);
      });
    }
    return qrPromise;
  };
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  const CUBE = '<svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2.8 20 7.3v9.4l-8 4.5-8-4.5V7.3z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><path d="M4.3 7.4 12 11.8l7.7-4.4M12 11.8v9" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/></svg>';

  class NfModelStage extends HTMLElement {
    connectedCallback() {
      if (this._init) return; // moving the element re-runs this
      this._init = true;
      // v20: a draft model (waiting for the owner's OK) only shows on preview themes
      const draftSection = this.closest('[data-nf3d-draft]');
      if (draftSection) {
        const th = window.Shopify && window.Shopify.theme;
        if (!th || th.role === 'main') return;
        draftSection.hidden = false;
      }
      if (this.dataset.placement === 'gallery' && this.moveIntoGallery()) this.watchGallery();
      this.viewer = this.querySelector('model-viewer');
      this.chips = Array.from(this.querySelectorAll('.nf3d__chip'));
      this.spots = Array.from(this.querySelectorAll('.nf3d__spot'));
      this.reset = this.querySelector('.nf3d__reset');
      this.home = {
        orbit: this.viewer.getAttribute('camera-orbit'),
        target: this.viewer.getAttribute('camera-target') || 'auto auto auto',
        fov: this.viewer.getAttribute('field-of-view'),
      };
      this.active = -1;
      // Square gallery slot or narrow screen: step the camera back so the whole product fits
      this.zoomOut = () => {
        const f = this.querySelector('.nf3d__frame') || this;
        const w = f.clientWidth, h = f.clientHeight || w;
        if (!w) return 1;
        if (w / h < 1.25) return 1.3;
        return w < 600 ? 1.5 : 1;
      };
      this.fit = (orbit) => (orbit || '').replace(/([\d.]+)m\s*$/, (m, r) => `${(parseFloat(r) * this.zoomOut()).toFixed(3)}m`);
      this.viewer.setAttribute('camera-orbit', this.fit(this.home.orbit));

      const start = () => {
        loadModelViewer().then(() => {
          this.viewer.addEventListener('load', () => { this.classList.add('is-loaded'); this.setupRoomView(); }, { once: true });
          this.viewer.addEventListener('error', () => this.classList.add('is-error'), { once: true });
          this.viewer.src = this.dataset.src;
          this.setAutoTurn(!reduce.matches);
        }).catch(() => this.classList.add('is-error'));
      };
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => {
          if (entries.some((e) => e.isIntersecting)) { io.disconnect(); start(); }
        }, { rootMargin: '600px 0px' });
        io.observe(this);
      } else start();

      // "How to move it" card: closed at first so the model is visible (v21); the button pulses softly
      // until the visitor opens the card, picks a part or starts moving the model
      this.badge = this.querySelector('.nf3d__badge');
      this.help = this.querySelector('.nf3d__help');
      this.badge?.addEventListener('click', () => this.toggleHelp(this.badge.getAttribute('aria-expanded') !== 'true'));
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') { this.toggleHelp(false); this.toggleQr(false); } });
      this.badge?.setAttribute('aria-expanded', 'false');
      if (this.gallery && this.help) this.badge?.classList.add('is-hinting');

      // Stop the slow turn as soon as the visitor moves the model
      this.viewer.addEventListener('camera-change', (e) => {
        if (e.detail && e.detail.source === 'user-interaction') {
          this.setAutoTurn(false);
          this.badge?.classList.remove('is-hinting');
        }
      });
      this.chips.forEach((c) => c.addEventListener('click', () => {
        const i = Number(c.dataset.spot);
        this.focus(i === this.active ? -1 : i);
      }));
      this.spots.forEach((s, i) => s.addEventListener('click', () => this.focus(i === this.active ? -1 : i)));
      this.reset?.addEventListener('click', () => this.focus(-1));
      this.viewer.addEventListener('dblclick', () => this.focus(-1));
      reduce.addEventListener?.('change', () => this.setAutoTurn(!reduce.matches && this.active < 0));
    }

    // v22: the theme rebuilds the gallery when the shopper picks another variant. Put the viewer back afterwards.
    watchGallery() {
      let queued = false;
      const check = () => {
        queued = false;
        const g = document.querySelector('media-gallery');
        if (!g) return;
        const ok = g === this.gallery && g.contains(this) && g.classList.contains('nf3d-gallery') &&
          (this.thumb ? this.thumb.isConnected : true) && (this.back ? this.back.isConnected : true);
        if (ok) return;
        if (this.moveIntoGallery()) {
          this.stale = true; // model-viewer stops drawing after being taken out of the page; reload it when shown again
          this.setMode('photos'); // show the chosen color's photo; "3D" brings the model back
        }
      };
      new MutationObserver(() => { if (!queued) { queued = true; requestAnimationFrame(check); } })
        .observe(document.body, { childList: true, subtree: true });
    }

    moveIntoGallery() {
      const gallery = document.querySelector('media-gallery');
      const slot = gallery && gallery.querySelector('slideshow-container');
      if (!slot) return false; // no gallery: stay in the section below
      // v22: clear what an earlier gallery left behind
      this.gnote?.remove(); this.thumb?.remove(); this.back?.remove();
      this.thumb = null; this.back = null;
      const section = this.closest('.nf3d');
      slot.classList.add('nf3d-host');
      gallery.classList.add('nf3d-gallery', 'is-3d');
      this.classList.add('nf3d--gallery');
      // honest note sits under the gallery while the 3D view shows
      if (this.dataset.note) {
        const n = document.createElement('p');
        n.className = 'nf3d__gnote';
        n.textContent = this.dataset.note;
        gallery.appendChild(n);
        this.gnote = n;
      }
      slot.appendChild(this);
      if (section) section.classList.add('is-moved');
      this.gallery = gallery;

      // "3D" button before the photo thumbnails (outside the theme's own thumbnail list)
      const thumbs = gallery.querySelector('.slideshow-controls__thumbnails');
      if (thumbs) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'nf3d-thumb';
        b.setAttribute('aria-pressed', 'true');
        b.setAttribute('aria-label', 'Show the 3D model');
        b.innerHTML = CUBE + '<span>3D</span>';
        thumbs.parentElement.insertBefore(b, thumbs);
        b.addEventListener('click', () => this.setMode('3d'));
        this.thumb = b;
        // any photo thumbnail switches to the photos
        thumbs.addEventListener('click', (e) => {
          if (e.target.closest('.slideshow-controls__thumbnail')) this.setMode('photos');
        });
      }
      // dots / arrows / counters on phones also mean "photos"
      gallery.querySelectorAll('slideshow-controls .slideshow-control:not(.slideshow-controls__thumbnail)').forEach((c) =>
        c.addEventListener('click', () => this.setMode('photos')));
      // a button to get back to 3D from the photos when there are no thumbnails
      if (!thumbs) {
        const back = document.createElement('button');
        back.type = 'button';
        back.className = 'nf3d-back';
        back.innerHTML = CUBE + '<span>View in 3D</span>';
        back.addEventListener('click', () => this.setMode('3d'));
        slot.appendChild(back);
        this.back = back;
      }
      return true;
    }

    // Room view (WebXR on Android Chrome): put the model back on the floor in front of the shopper.
    // Uses model-viewer 4.0.0's AR renderer (version pinned above). If it isn't there, the button stays hidden.
    arRenderer() {
      let p = this.viewer, sym = null;
      while (p && !sym) {
        sym = Object.getOwnPropertySymbols(p).find((s) => s.description === 'renderer') || null;
        p = Object.getPrototypeOf(p);
      }
      const ar = sym ? this.viewer[sym]?.arRenderer : null;
      return ar && 'goalPosition' in ar && 'goalYaw' in ar ? ar : null;
    }

    bringHere() {
      const ar = this.arRenderer();
      const scene = ar?.presentedScene;
      if (!scene) return;
      const e = scene.getCamera().matrixWorld.elements; // camera looks down its -Z axis
      let fx = -e[8], fz = -e[10];
      const len = Math.hypot(fx, fz) || 1;
      fx /= len; fz /= len;
      const dist = Math.max(0.5, 2.2 * (scene.boundingSphere?.radius || 0.2));
      ar.goalPosition.x = e[12] + fx * dist;
      ar.goalPosition.z = e[14] + fz * dist;
      const theta = this.viewer.getCameraOrbit?.().theta || 0;
      ar.goalYaw = Math.atan2(-fx, -fz) - theta; // same facing as when it was first placed
      ar.goalScale = 1;
    }

    // Phones with AR get model-viewer's own "See it in your room" button.
    // Computers can't do AR, so offer a QR code that opens this page on a phone.
    setupRoomView() {
      const here = this.querySelector('.nf3d__arhere');
      if (here && this.arRenderer()) {
        here.addEventListener('click', (e) => { e.stopPropagation(); this.bringHere(); });
      } else if (here) {
        here.closest('.nf3d__arbar').classList.add('no-here');
      }
      this.qrBtn = this.querySelector('.nf3d__qrbtn');
      this.qr = this.querySelector('.nf3d__qr');
      if (!this.qrBtn || !this.qr) return;
      if (this.viewer.canActivateAR || !finePointer.matches) return;
      this.qrBtn.hidden = false;
      this.qrBtn.addEventListener('click', () => this.toggleQr(this.qr.hidden));
      this.querySelector('.nf3d__qrclose')?.addEventListener('click', () => { this.toggleQr(false); this.qrBtn.focus(); });
    }

    toggleQr(open) {
      if (!this.qr || !this.qrBtn || this.qrBtn.hidden) return;
      this.qr.hidden = !open;
      this.qrBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (!open) return;
      this.toggleHelp(false);
      const box = this.qr.querySelector('.nf3d__qrcode');
      if (box && !box.firstChild) {
        loadQr().then(() => {
          const q = window.qrcode(0, 'M');
          let url = this.dataset.pageUrl || location.href.split('?')[0];
          const th = window.Shopify && window.Shopify.theme;
          if (th && th.id && th.role && th.role !== 'main') url += (url.includes('?') ? '&' : '?') + 'preview_theme_id=' + th.id; // preview copy opens on the phone too
          q.addData(url);
          q.make();
          box.innerHTML = q.createSvgTag({ cellSize: 4, margin: 2, scalable: true });
        }).catch(() => { box.textContent = 'Open this page on your phone.'; });
      }
    }

    setMode(mode) {
      if (!this.gallery) return;
      const is3d = mode === '3d';
      this.hidden = !is3d;
      this.gallery.classList.toggle('is-3d', is3d);
      this.thumb?.setAttribute('aria-pressed', is3d ? 'true' : 'false');
      if (is3d && this.stale && this.viewer.src) {
        this.stale = false;
        const src = this.viewer.src;
        this.viewer.src = null;
        this.viewer.src = src; // same file, from the browser cache
      }
      if (!is3d) { this.setAutoTurn(false); this.toggleQr(false); }
    }

    toggleHelp(open) {
      if (!this.badge || !this.help) return;
      this.badge.classList.remove('is-hinting');
      this.badge.setAttribute('aria-expanded', open ? 'true' : 'false');
      this.help.classList.toggle('is-open', open);
    }

    setAutoTurn(on) {
      this.viewer.autoRotate = on;
      if (on) {
        this.viewer.autoRotateDelay = 2500;
        this.viewer.rotationPerSecond = '14deg';
      }
    }

    focus(i) {
      this.active = i;
      if (i >= 0) this.toggleHelp(false); // a part button means they know how it works
      this.setAutoTurn(false);
      // auto-turn spins the model itself; put it back so the part views line up
      this.viewer.resetTurntableRotation?.(0);
      this.chips.forEach((c, j) => c.setAttribute('aria-pressed', j === i ? 'true' : 'false'));
      this.spots.forEach((s, j) => s.classList.toggle('is-active', j === i));
      const v = this.viewer;
      if (i < 0) {
        v.cameraTarget = this.home.target;
        v.cameraOrbit = this.fit(this.home.orbit);
        v.fieldOfView = this.home.fov;
        if (reduce.matches) v.jumpCameraToGoal?.();
        return;
      }
      const s = this.spots[i];
      v.cameraTarget = s.dataset.target || s.dataset.position;
      if (s.dataset.orbit) v.cameraOrbit = this.fit(s.dataset.orbit);
      v.fieldOfView = '22deg';
      if (reduce.matches) v.jumpCameraToGoal?.(); // no camera glide for reduced motion
    }
  }
  if (!customElements.get('nf-model-stage')) customElements.define('nf-model-stage', NfModelStage);
})();
